import { CacheStrategy, DataSourceStrategy } from '@rammetec/data-access';
import { AUDIT_FIELD_DESCRIPTORS, AUDIT_FOREIGN_KEYS } from './_auditFields.js';
import { LIFECYCLE_FIELD_DESCRIPTORS } from './_lifecycleFields.js';
import { SYSTEM_ALLOWED_DATA_SOURCES } from './dataSourcePolicy.js';
import { USER_DATA, ORGANIZATION_USER_ORG_DATA } from '../types/user.js';
import { FIELD_LIMITS } from '../validation/fieldLimits.js';
/**
 * Descriptor for UserData entity.
 * Stored in the global 'user' collection.
 *
 * Fields without groups are always persisted.
 * Fields with groups are persisted only when those groups are active.
 */
export const userDataDescriptor = {
    name: USER_DATA,
    label: 'Usuário',
    domain: 'identity',
    collectionPath: 'user',
    version: 1,
    description: 'Represents a user in the system with global information',
    repositoryConfig: {
        dataSource: DataSourceStrategy.Firestore,
        cache: CacheStrategy.InMemory,
    },
    allowedDataSources: SYSTEM_ALLOWED_DATA_SOURCES,
    fields: [
        {
            name: 'id',
            label: 'ID',
            type: 'string',
            required: true,
            // No groups = always persisted
            description: 'Unique user identifier (Firebase UID)',
        },
        {
            name: 'email',
            label: 'E-mail',
            type: 'string',
            required: false,
            description: 'User email address',
        },
        {
            name: 'name',
            label: 'Nome',
            type: 'string',
            required: false,
            maxLength: FIELD_LIMITS.NAME,
            description: 'User display name',
        },
        {
            name: 'gender',
            label: 'Sexo',
            type: 'enum',
            required: false,
            enumValues: ['Homem', 'Mulher'],
            description: 'User gender',
        },
        ...LIFECYCLE_FIELD_DESCRIPTORS,
        {
            name: 'currentOrg',
            label: 'Organização atual',
            type: 'string',
            required: false,
            description: 'ID of the currently selected organization',
        },
        ...AUDIT_FIELD_DESCRIPTORS,
    ],
    foreignKeys: [
        ...AUDIT_FOREIGN_KEYS,
        {
            fieldName: 'currentOrg',
            targetEntity: 'OrganizationData',
            relationship: 'one-to-one',
            description: 'The organization the user is currently working in',
        },
    ],
};
/**
 * Descriptor for OrganizationUserOrgData entity.
 * Stored in 'org/{orgId}/users/{userId}'.
 * Contains organization-specific user information.
 */
export const organizationUserOrgDataDescriptor = {
    name: ORGANIZATION_USER_ORG_DATA,
    label: 'Usuário da organização',
    domain: 'identity',
    collectionPath: 'org/{orgId}/users',
    version: 1,
    description: 'Organization-specific user data (role, profile, status within an org)',
    repositoryConfig: {
        dataSource: DataSourceStrategy.Firestore,
        cache: CacheStrategy.InMemory,
    },
    allowedDataSources: SYSTEM_ALLOWED_DATA_SOURCES,
    fields: [
        {
            name: 'id',
            label: 'ID',
            type: 'string',
            required: true,
            // No groups = always persisted
            description: 'User ID within this organization',
        },
        {
            name: 'role',
            label: 'Papel',
            type: 'enum',
            required: false,
            enumValues: ['user', 'supervisor', 'admin', 'system'],
            defaultValue: 'user',
            description: 'User role within this organization',
        },
        {
            name: 'currentProfile',
            label: 'Perfil atual',
            type: 'string',
            required: false,
            // Session profile switch — not part of the field-audit funnel (request 0257).
            audit: 'off',
            description: 'ID of the currently selected profile',
        },
        ...LIFECYCLE_FIELD_DESCRIPTORS,
        {
            name: 'allowOwnProfile',
            label: 'Permitir perfil próprio',
            type: 'boolean',
            required: false,
            defaultValue: false,
            description: 'Whether user can have a personal profile in this org',
        },
        ...AUDIT_FIELD_DESCRIPTORS,
    ],
    foreignKeys: [
        ...AUDIT_FOREIGN_KEYS,
        {
            fieldName: 'currentProfile',
            targetEntity: 'ProfileData',
            relationship: 'one-to-one',
            description: 'The profile the user is currently using',
        },
    ],
};
//# sourceMappingURL=user.descriptor.js.map